import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import type { DisplayDocument, Mode, SkillInfo, UsageSummary } from '../../types/agent';
import { ACCEPT_ATTRIBUTE, useAttachments, type AttachmentsApi } from '../../hooks/useAttachments';
import { AttachmentChips } from './AttachmentChips';
import { ModeSwitch } from './ModeSwitch';
import { matchSkills, SkillMenu } from './SkillMenu';
import { money, UsageBar, UsagePlaceholder } from './UsageBar';

export { money, usageTitle } from './UsageBar';

/** Design 1a/1b composer: chips row above a white box with an auto-growing textarea, a mono hint
 *  line (shortcuts / Working…) and the ↑ send or ■ stop control, the usage bar under the box. Files (PDF, Word, Excel,
 *  CSV, text, images) attach through the paperclip, drag-and-drop or paste (2026-09-10): uploaded at once,
 *  sent by id with the message. A draft starting with `/`, or the `/` button, opens the Skills menu. Last, the line
 *  that says Sculpt is an AI and can be wrong. */
export function Composer({ chips, skills = [], disabled, lockedHint, answering, busy, usage, admin = false, mode, onModeChange, modelSwitch, onSend, onStop, api, threadId, placement = 'bottom', outerRef, onDraftChange }: { chips: ReactNode; /** The Skills the `/` menu lists (`useSkills`); none: no menu, no button. */ skills?: SkillInfo[]; disabled: boolean; /** Placeholder while `disabled` for a reason other than the spend limit (the workbook index). */ lockedHint?: string | null; /** A Question card is waiting: what is typed here answers it (PLAN-questions.md D10). */ answering?: boolean; busy: boolean; usage: UsageSummary | null; /** An owner or admin: the usage bar offers "Upgrade". */ admin?: boolean; mode: Mode; onModeChange: (m: Mode) => void; /** The model picker (`ModelSwitch`), next to the mode. */ modelSwitch?: ReactNode; onSend: (text: string, documents: DisplayDocument[]) => void; onStop: () => void; api: AttachmentsApi; threadId: string | null; /** `center`: the empty-thread layout (no top border, centered, capped width). */ placement?: 'bottom' | 'center'; /** The outer element (ChatPanel animates its dock from the centre to the bottom). */ outerRef?: Ref<HTMLDivElement>; /** Surfaced so the feedback ribbon can hold off while the user is mid-sentence. */ onDraftChange?: (dirty: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const threadRef = useRef(threadId);
  threadRef.current = threadId;
  const attachments = useAttachments(api, () => threadRef.current);
  // The `/` menu: open while the draft is a slash and a name being typed, or after the `/` button; Escape closes it
  // until the draft leaves that shape.
  const [menuForced, setMenuForced] = useState(false);
  const [menuDismissed, setMenuDismissed] = useState(false);
  const [menuActive, setMenuActive] = useState(0);
  const slash = /^\/(\S*)$/.exec(draft);
  const query = slash ? slash[1] : menuForced ? '' : null;
  const menuSkills = query === null ? [] : matchSkills(skills, query);
  const menuOpen = !disabled && !menuDismissed && menuSkills.length > 0;

  useEffect(() => {
    setMenuActive(0);
    if (!/^\/\S*$/.test(draft)) setMenuDismissed(false);
  }, [draft]);

  const pickSkill = (name: string) => {
    // Replace a `/name` already typed at the start, keep the rest of the draft.
    setDraft(`/${name} ${draft.replace(/^\/\S*\s*/, '')}`);
    setMenuForced(false);
    ref.current?.focus();
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(100, el.scrollHeight)}px`;
  }, [draft]);

  // Surfaced so the feedback card can hold off while the user is mid-sentence.
  useEffect(() => {
    onDraftChange?.(draft.trim().length > 0);
  }, [draft, onDraftChange]);

  const canSend = draft.trim().length > 0 && !busy && !disabled && !attachments.uploading;
  const submit = () => {
    if (!canSend) return;
    const text = draft.trim();
    setDraft('');
    setMenuForced(false);
    onSend(text, attachments.take());
  };
  const hint = busy ? t('composer.working') : attachments.items.some((a) => a.status === 'processing') ? t('attachments.reading') : attachments.uploading ? t('attachments.uploading') : t('composer.hint');
  const locale = i18n?.language ?? 'en';
  const currency = usage?.currency ?? 'USD';
  const limitText = usage?.limit_reached ? t(`composer.limit.${usage.reason ?? 'legacy'}`, { cap: money(usage.user_cap ?? usage.member_cap ?? 0, currency, locale) }) : null;

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    attachments.add(Array.from(e.target.files ?? []));
    e.target.value = '';
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    attachments.add(Array.from(e.dataTransfer.files ?? []));
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    if (!dragging) setDragging(true);
  };

  return (
    <div ref={outerRef} className={placement === 'center' ? 'flex w-full max-w-[560px] shrink-0 flex-col gap-2 self-center px-4' : 'flex shrink-0 flex-col gap-2 border-t border-line-soft px-3 pb-3 pt-2.5'} data-placement={placement}>
      {chips}
      <AttachmentChips items={attachments.items} onRemove={attachments.remove} />
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={() => setDragging(false)}
        className={`relative flex flex-col gap-1.5 rounded-lg border bg-white py-2 pl-3 pr-2 shadow-[0_1px_2px_rgba(0,0,0,.03)] transition-[border-color] ${dragging ? 'border-dashed border-accent bg-accent-bg' : focused ? 'border-accent' : 'border-line'}`}
      >
        {menuOpen && <SkillMenu skills={menuSkills} active={menuActive} onPick={pickSkill} onHover={setMenuActive} />}
        <textarea
          ref={ref}
          value={draft}
          rows={1}
          disabled={disabled}
          placeholder={disabled ? (lockedHint ?? limitText ?? t('composer.limit.legacy')) : answering ? t('composer.answering') : attachments.items.length ? t('attachments.placeholder') : t('composer.placeholder')}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setMenuForced(false);
          }}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData?.files ?? []);
            if (files.length) {
              e.preventDefault();
              attachments.add(files);
            }
          }}
          onKeyDown={(e) => {
            if (menuOpen) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const step = e.key === 'ArrowDown' ? 1 : -1;
                setMenuActive((i) => (i + step + menuSkills.length) % menuSkills.length);
                return;
              }
              if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
                e.preventDefault();
                pickSkill(menuSkills[Math.min(menuActive, menuSkills.length - 1)].name);
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setMenuDismissed(true);
                setMenuForced(false);
                return;
              }
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          className="max-h-[100px] min-h-[20px] w-full resize-none border-0 bg-transparent p-0 text-[13px] leading-5 text-ink placeholder:text-fainter focus:ring-0 disabled:text-faint"
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <ModeSwitch mode={mode} onChange={onModeChange} disabled={busy} placement="up" />
            {modelSwitch}
            <input ref={fileInput} type="file" multiple accept={ACCEPT_ATTRIBUTE} onChange={onPick} className="hidden" data-testid="attachment-input" />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={disabled || busy}
              title={t('attachments.attach')}
              aria-label={t('attachments.attach')}
              className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-sm text-faint hover:bg-hover hover:text-ink disabled:opacity-40"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            {skills.length > 0 && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setMenuDismissed(false);
                  setMenuForced((v) => !v);
                  ref.current?.focus();
                }}
                disabled={disabled || busy}
                title={t('skills.button')}
                aria-label={t('skills.button')}
                aria-expanded={menuOpen}
                className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-sm font-mono text-[13px] text-faint hover:bg-hover hover:text-ink disabled:opacity-40"
              >
                /
              </button>
            )}
            <div className="truncate font-mono text-[11px] text-faint">{hint}</div>
          </div>
          {busy ? (
            <button type="button" onClick={onStop} title={t('composer.stop')} aria-label={t('composer.stop')} className="grid h-[26px] w-[26px] shrink-0 animate-pc-pop place-items-center rounded-md bg-ink transition-transform active:scale-90">
              <div className="h-[9px] w-[9px] rounded-[1px] bg-white" />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={!canSend} title={t('composer.send')} aria-label={t('composer.send')} className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-md text-[13px] text-white transition-[background-color,transform] duration-150 active:scale-90 ${canSend ? 'bg-accent hover:bg-accent-hover' : 'bg-send-idle'}`}>
              ↑
            </button>
          )}
        </div>
      </div>
      {usage ? <UsageBar usage={usage} admin={admin} /> : <UsagePlaceholder />}
      <p className="px-0.5 text-center text-[10.5px] leading-4 text-fainter" data-testid="composer-disclaimer">{t('composer.disclaimer')}</p>
    </div>
  );
}
